/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS migration — Executive dashboard. Mirrors the reference app's
   (dashboard)/dashboard/page.tsx section by section: Shipments & Revenue
   (Active Brands + Open Pipeline drills), Sales Funnel, Trends & Performance,
   Channel & Brand Analysis, Revenue Concentration & Risk, At-Risk / Churned /
   Declining, Brand Revenue by Age, Sales POC Performance, Revenue Projection.
   ProShip sections carry the "ProShip: <from – data-through>" tag, HubSpot
   sections the "HubSpot: <from – last sync>" tag (backend coverage windows).
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useGetExecutiveDashboardQuery } from '../../redux/growthosExecutiveApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import { GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_PAGE_SX, GxSyncStatusBar } from '../growthos-shared/GrowthOsUi'
import { GROUPED_METRIC_GROUPS, GxPagedTable, groupedMetricColumns, periodLeadGroup } from '../growthos-shared/GxPagedTable'
import {
  GrowthPct, comparison, compactCurrency, currency, daysAgoLabel, integer, monthLabel, percentage, shortDate,
} from '../growthos-shared/gxFormat'
import {
  ACCENT, ACCENT_GRAD, DONUT_COLORS, INK_400, ChartTooltip, KpiCard, ScopeTag, SectionTitle, TaperedFunnel, cardSx, tile3dSx,
} from './execUi'

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]

const kpiGridSx = { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' } }
const subTitleSx = { fontSize: 14, fontWeight: 600, color: GX_INK_700 }
const chevron = (open, size = 14) => <ExpandMoreIcon sx={{ fontSize: size, transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }} />
const pctOf = (n, d) => (d > 0 ? (n / d) * 100 : 0)

// Grouped-metric pickers: the DTO names its fields cur*/prev* (brand rows) or revenue/prev* (health rows).
const pickBrand = (b) => ({ prevShipments: b.prevShipments, shipments: b.curShipments, shipmentChangePct: b.shipmentChangePct, prevRevenue: b.prevRevenue, revenue: b.curRevenue, revenueChangePct: b.revenueChangePct })
const pickHealth = (r) => ({ prevShipments: r.prevShipments, shipments: r.shipments, shipmentChangePct: r.shipmentChangePct, prevRevenue: r.prevRevenue, revenue: r.revenue, revenueChangePct: r.revenueChangePct })

const brandCol = { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <Box component="span" sx={{ fontWeight: 500 }}>{b.brandName}</Box> }
const lastShipCol = (get) => ({ key: 'lastShip', header: 'Last shipment', title: "The brand's most recent positive shipment, measured against the ProShip data-through date.", align: 'right', sortValue: (b) => get(b) ?? -1, cell: (b) => daysAgoLabel(get(b)) })
const dealClosedCol = { key: 'dealClosed', header: 'Deal Closed date', title: "Close date of the brand's won deal (HubSpot), if linked.", sortValue: (b) => b.dealClosedDate ?? '', cell: (b) => (b.dealClosedDate ? shortDate(b.dealClosedDate) : '—') }

function DetailBlock({ title, subtitle, children }) {
  return (
    <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${GX_BORDER}` }}>
      <Typography sx={{ mb: 0.5, ...subTitleSx }}>{title}</Typography>
      <Typography sx={{ mb: 1.5, fontSize: 12, color: GX_INK_500 }}>{subtitle}</Typography>
      {children}
    </Box>
  )
}

function ChartHeader({ title, tag }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      <Typography sx={subTitleSx}>{title}</Typography>
      {tag}
    </Box>
  )
}

function OpenPipelineDrill({ byPipeline }) {
  const [openPipeline, setOpenPipeline] = useState(null)
  const [openPoc, setOpenPoc] = useState(null)
  const pocKey = (pl, poc) => `${pl.pipeline}|${poc.personId ?? poc.owner}`
  return (
    <GxPagedTable variant="executive"
      rows={byPipeline || []}
      rowKey={(pl) => pl.pipeline}
      emptyText="No open hot deals right now."
      onRowClick={(pl) => { setOpenPipeline(openPipeline === pl.pipeline ? null : pl.pipeline); setOpenPoc(null) }}
      isRowActive={(pl) => openPipeline === pl.pipeline}
      columns={[
        { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline. Click a row for the Sales POC breakdown.', sortValue: (pl) => pl.pipeline,
          cell: (pl) => <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>{chevron(openPipeline === pl.pipeline)}{pl.pipeline}</Box> },
        { key: 'deals', header: 'Hot Deals', title: 'Currently-open HOT deals in this pipeline (any create date).', sortValue: (pl) => pl.deals, cell: (pl) => integer(pl.deals) },
        { key: 'estMbr', header: 'Est. MBR', title: 'Σ Estimated MBR (₹/month) of those hot deals.', sortValue: (pl) => pl.estimatedMbr, cell: (pl) => currency(pl.estimatedMbr) },
      ]}
      renderExpanded={(pl) => (openPipeline === pl.pipeline ? (
        <Box sx={{ bgcolor: '#f8fafc', p: 1.5 }}>
          <GxPagedTable variant="executive"
            rows={pl.byPoc || []}
            rowKey={(poc) => pocKey(pl, poc)}
            onRowClick={(poc) => { const k = pocKey(pl, poc); setOpenPoc(openPoc === k ? null : k) }}
            isRowActive={(poc) => openPoc === pocKey(pl, poc)}
            columns={[
              { key: 'owner', header: 'Sales POC', title: 'Deal owner (Sales POC). Click a row for the deal list.', sortValue: (poc) => poc.owner,
                cell: (poc) => <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>{chevron(openPoc === pocKey(pl, poc), 13)}{poc.owner}</Box> },
              { key: 'team', header: 'Team', title: "The POC's team tag from Data → Sales POCs.", sortValue: (poc) => poc.team ?? '', cell: (poc) => poc.team ?? '—' },
              { key: 'deals', header: 'Hot Deals', title: 'Open hot deals owned in this pipeline.', sortValue: (poc) => poc.deals, cell: (poc) => integer(poc.deals) },
              { key: 'estMbr', header: 'Est. MBR', title: 'Σ Estimated MBR (₹/month).', sortValue: (poc) => poc.estimatedMbr, cell: (poc) => currency(poc.estimatedMbr) },
            ]}
            renderExpanded={(poc) => (openPoc === pocKey(pl, poc) ? (
              <Box sx={{ bgcolor: '#fff', p: 1.5 }}>
                <GxPagedTable variant="executive"
                  rows={poc.dealList || []}
                  rowKey={(dl) => dl.dealId}
                  columns={[
                    { key: 'deal', header: 'Deal', title: 'HubSpot deal name.', sortValue: (dl) => dl.dealName, cell: (dl) => <Box component="span" sx={{ fontWeight: 500 }}>{dl.dealName}</Box> },
                    { key: 'stage', header: 'Stage', title: 'Current pipeline stage.', sortValue: (dl) => dl.stage ?? '', cell: (dl) => dl.stage ?? '—' },
                    { key: 'estMbr', header: 'Est. MBR', title: 'Estimated MBR (₹/month) on the deal.', sortValue: (dl) => dl.estimatedMbr, cell: (dl) => currency(dl.estimatedMbr) },
                    { key: 'hubspot', header: 'HubSpot', title: 'Opens the deal record in HubSpot.',
                      cell: (dl) => <Box component="a" href={dl.hubspotUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#4338ca', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Deal <OpenInNewIcon sx={{ fontSize: 12 }} /></Box> },
                  ]}
                />
              </Box>
            ) : null)}
          />
        </Box>
      ) : null)}
    />
  )
}

function BrandHealthTable({ rows, timeframe }) {
  return (
    <GxPagedTable variant="executive"
      rows={rows}
      rowKey={(r) => r.brandId}
      maxBodyHeight={512}
      emptyText="No brands meet this condition in the selected window."
      headerGroups={[periodLeadGroup(2, timeframe), ...GROUPED_METRIC_GROUPS, { label: '', span: 2 }]}
      columns={[
        brandCol,
        { key: 'poc', header: 'Sales POC', title: "The account's Sales POC — the Data → Brands assignment on ProShip tables.", sortValue: (r) => r.poc, cell: (r) => r.poc },
        ...groupedMetricColumns(pickHealth),
        lastShipCol((r) => r.daysSinceShipment),
        { key: 'risk', header: 'Risk', sortValue: (r) => r.risk, cell: (r) => <Box component="span" sx={{ display: 'inline-flex', borderRadius: '9999px', border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', px: 1.5, py: 0.5, fontSize: 12, fontWeight: 500, color: GX_INK_700 }}>{r.risk}</Box> },
      ]}
    />
  )
}

function CohortTable({ rows }) {
  return (
    <GxPagedTable variant="executive"
      rows={rows}
      rowKey={(r) => r.brandId}
      maxBodyHeight={512}
      emptyText="No brands in this cohort for the selected window."
      columns={[
        brandCol,
        { key: 'poc', header: 'Sales POC', title: "The account's Sales POC — the Data → Brands assignment on ProShip tables.", sortValue: (r) => r.poc, cell: (r) => r.poc },
        { key: 'revenue', header: 'Revenue', title: 'ProShip revenue (₹) in the selected window.', sortValue: (r) => r.revenue, cell: (r) => currency(r.revenue) },
        { key: 'shipments', header: 'Shipments', title: 'ProShip shipments in the selected window.', sortValue: (r) => r.shipments, cell: (r) => integer(r.shipments) },
        { key: 'firstShip', header: 'First Shipment', sortValue: (r) => r.firstShipmentDate ?? '', cell: (r) => (r.firstShipmentDate ? shortDate(r.firstShipmentDate) : '—') },
        { key: 'age', header: 'Brand Age', sortValue: (r) => r.ageMonths ?? -1, cell: (r) => (r.ageMonths == null ? '—' : `${integer(r.ageMonths)} months`) },
      ]}
    />
  )
}

export default function ExecutivePage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [poc, setPoc] = useState('')
  const [snapDrill, setSnapDrill] = useState(null) // 'activeBrands' | 'openPipeline'
  const [funnelDrill, setFunnelDrill] = useState(null) // 'customersClosed' | 'newBrandsLive'
  const [openBrandDetail, setOpenBrandDetail] = useState(null) // { type: 'risk'|'cohort', key, title }
  const [pocTableOpen, setPocTableOpen] = useState(false)
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, isFetching, error } = useGetExecutiveDashboardQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined, poc: poc || undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const loading = isFetching && !data

  const snapshot = data?.snapshot
  const funnel = data?.funnel
  const proship = data?.coverage?.proship
  const hubspot = data?.coverage?.hubspot
  const proshipTag = proship ? <ScopeTag label={`ProShip: ${proship.label}`} title={`Actuals are capped at ProShip data through ${proship.dataThrough}; comparison uses the equivalent prior period.`} /> : null
  const monthlyChart = (data?.trend?.monthly || []).map((r) => ({ ...r, name: monthLabel(r.month) }))
  const weeklyChart = data?.trend?.weekly || []
  const topBrandsChart = (data?.topBrands || []).map((b) => ({ ...b, name: b.brandName && b.brandName.length > 18 ? `${b.brandName.slice(0, 17)}…` : b.brandName }))
  const dealsBySource = data?.dealsBySource
  const concentration = data?.concentration
  const projection = data?.projection
  const brandHealth = data?.brandHealth
  const toggleSnap = (k) => setSnapDrill((c) => (c === k ? null : k))
  const toggleFunnel = (k) => setFunnelDrill((c) => (c === k ? null : k))
  const lastWeekEnd = weeklyChart.length
    ? new Date(Date.parse(`${weeklyChart[weeklyChart.length - 1].week}T00:00:00Z`) + 6 * 86400000).toISOString().slice(0, 10)
    : null
  const hhiColor = concentration?.hhiLabel === 'High' ? '#e11d48' : concentration?.hhiLabel === 'Moderate' ? '#d97706' : '#059669'

  return (
    <Box sx={{ ...GX_PAGE_SX, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ mb: -1 }}><GxSyncStatusBar runs={runsData?.runs || []} /></Box>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', color: GX_INK_900 }}>Executive Dashboard</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: GX_INK_500 }}>The whole growth engine — all HubSpot pipelines, all ProShip-shipped brands.</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 144, fontSize: 14, bgcolor: '#fff', borderRadius: '12px' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} inputProps={{ 'aria-label': 'Custom date from' }} sx={{ bgcolor: '#fff' }} />
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} inputProps={{ 'aria-label': 'Custom date to' }} sx={{ bgcolor: '#fff' }} />
            </>
          )}
          <Select size="small" value={poc} onChange={(e) => setPoc(e.target.value)} displayEmpty
            sx={{ minWidth: 160, fontSize: 14, bgcolor: '#fff', borderRadius: '12px' }}
            renderValue={(v) => {
              if (!v) return 'All Sales POCs'
              const p = (data?.pocOptions || []).find((o) => o.personId === v)
              return p ? p.name : v
            }}>
            <MenuItem value="">All Sales POCs</MenuItem>
            {(data?.pocOptions || []).map((p) => <MenuItem key={p.personId} value={p.personId}>{p.name}</MenuItem>)}
          </Select>
          {data?.window && <ScopeTag size={13} label={`Showing: ${data.window.label}`} title={`${data.window.from} → ${data.window.to}`} />}
        </Box>
      </Box>

      {error && (
        <Box sx={{ borderRadius: '16px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', p: 2, fontSize: 14, color: '#be123c' }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {!loading && data && !data.hasData && (
        <Box sx={{ ...cardSx, p: 4, textAlign: 'center', fontSize: 14, color: GX_INK_500 }}>
          <Typography sx={{ fontWeight: 600, color: GX_INK_700 }}>No ProShip shipment data yet</Typography>
          Run a ProShip sync from Data → Connectors to populate this dashboard.
        </Box>
      )}

      {/* ── Shipments & Revenue ────────────────────────────────────────────── */}
      <Box>
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<TrendingUpIcon />}>Shipments &amp; Revenue</SectionTitle>
          {proshipTag}
        </Box>
        <Box sx={kpiGridSx}>
          <KpiCard label="Total Shipments" value={snapshot ? integer(snapshot.shipments.value) : undefined} sub={comparison(snapshot?.shipments, integer)} accent="indigo" icon={<Inventory2OutlinedIcon />} loading={loading}
            definition="Σ ProShip shipments across all non-excluded brands in the selected window." />
          <KpiCard label="Total Revenue" value={snapshot ? currency(snapshot.revenue.value) : undefined} sub={comparison(snapshot?.revenue, currency)} accent="teal" icon={<CurrencyRupeeIcon />} loading={loading}
            definition="Σ ProShip revenue across all non-excluded brands in the selected window." />
          <KpiCard label="Active Brands" value={snapshot ? integer(snapshot.activeBrands.value) : undefined} sub={snapshot ? `${comparison(snapshot.activeBrands, integer)} · click` : undefined} accent="amber" icon={<StorefrontOutlinedIcon />} loading={loading}
            onClick={() => toggleSnap('activeBrands')} active={snapDrill === 'activeBrands'}
            definition="Brands with at least one shipment in the selected window. Click for the brand list with revenue & shipment growth vs the comparable prior window. Count includes hidden-from-display brands; the list shows visible ones." />
          <KpiCard label="Open Pipeline (Hot)" value={snapshot ? compactCurrency(snapshot.openPipeline.estimatedMbr) : undefined}
            sub={snapshot ? `${integer(snapshot.openPipeline.deals)} hot mktg deals · weighted ${compactCurrency(snapshot.openPipeline.weightedMbr)} · click` : undefined}
            accent="rose" icon={<TrendingUpIcon />} loading={loading} onClick={() => toggleSnap('openPipeline')} active={snapDrill === 'openPipeline'}
            definition="Σ Estimated MBR of currently-open HOT, MARKETING-SOURCED deals across every pipeline — a live snapshot, independent of the period filter. Weighted = stage-probability-adjusted. Click to drill Pipeline → Sales POC → deal (with HubSpot links)." />
        </Box>

        {snapDrill === 'openPipeline' && snapshot && (
          <Box sx={{ mt: 2 }}><OpenPipelineDrill byPipeline={snapshot.openPipeline.byPipeline} /></Box>
        )}
        {snapDrill === 'activeBrands' && data && (
          <Box sx={{ mt: 2 }}>
            <GxPagedTable variant="executive"
              rows={data.activeBrandsList || []}
              rowKey={(b) => b.brandId}
              emptyText="No active brands in this window."
              headerGroups={[periodLeadGroup(2, proship?.label), ...GROUPED_METRIC_GROUPS, { label: '', span: 2 }]}
              columns={[
                { ...brandCol, title: 'Brand with ≥1 positive shipment in the window.' },
                { key: 'poc', header: 'Sales POC', title: "The brand's assigned Sales POC (Data → Brands).", sortValue: (b) => b.salesPoc, cell: (b) => b.salesPoc },
                ...groupedMetricColumns(pickBrand),
                lastShipCol((b) => b.daysSinceShipment),
                dealClosedCol,
              ]}
            />
          </Box>
        )}
      </Box>

      {/* ── Sales Funnel (d2c-shell) ───────────────────────────────────────── */}
      <Box component="section" sx={{
        position: 'relative', overflow: 'hidden', borderRadius: '28px', p: 3, border: '1px solid rgba(79,70,229,0.14)',
        background: 'linear-gradient(160deg, #eef2ff 0%, #ffffff 55%, #f5f3ff 100%)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 48px -22px rgba(79,70,229,0.38), 0 8px 18px -10px rgba(15,23,42,0.12)',
        '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 4, background: ACCENT_GRAD },
      }}>
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <SectionTitle icon={<TrackChangesIcon />}>Sales Funnel</SectionTitle>
          {hubspot && <ScopeTag label={`HubSpot: ${hubspot.label}`} title={`HubSpot metrics are capped at its latest completed sync date (${hubspot.dataThrough}).`} />}
        </Box>
        {!loading && funnel && (
          <TaperedFunnel
            minWidthPct={44}
            stages={[
              { key: 'totalLeads', label: 'Total Leads', count: funnel.totalLeads, pct: 100, icon: <PeopleOutlineIcon />, gradient: 'linear-gradient(135deg,#4F46E5,#06B6D4)', shadow: 'rgba(79,70,229,0.45)' },
              { key: 'opportunities', label: 'Opportunity', count: funnel.opportunities, pct: funnel.oppPctOfLeads, icon: <TrackChangesIcon />, gradient: 'linear-gradient(135deg,#0D9488,#34D399)', shadow: 'rgba(13,148,136,0.4)' },
              { key: 'customersNew', label: 'Customers · New', count: funnel.customersNew, pct: funnel.customersPctOfLeads, icon: <EmojiEventsOutlinedIcon />, gradient: 'linear-gradient(135deg,#7C3AED,#C084FC)', shadow: 'rgba(124,58,237,0.4)' },
            ]}
          />
        )}
        <Typography sx={{ mx: 'auto', mt: 1.5, maxWidth: 672, textAlign: 'center', fontSize: 12, color: INK_400 }}>
          {poc ? 'Total Leads is all sources — not POC-scoped (leads aren’t attributed to a Sales POC). ' : ''}
          Each stage is a strict subset of the one above (same created-in-window cohort). Customers · Closed (won-in-window, any create date) is on the tile below.
        </Typography>
        <Box sx={{ mt: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
          <KpiCard label="Work In Progress" value={funnel ? integer(funnel.wip) : undefined} sub="Created in window, still open — a live branch of Opportunity" accent="amber" icon={<AccessTimeIcon />} loading={loading} />
          <KpiCard label="Customers · Closed" value={funnel ? integer(funnel.customersClosed) : undefined} sub="Won in window, any create date · click for deals" accent="indigo" icon={<EmojiEventsOutlinedIcon />} loading={loading}
            onClick={() => toggleFunnel('customersClosed')} active={funnelDrill === 'customersClosed'} />
          <KpiCard label="New Active Brands" value={data ? integer(data.newActiveBrands) : undefined} sub="1st shipment in window · click" accent="teal" icon={<AutoAwesomeOutlinedIcon />} loading={loading}
            onClick={() => toggleFunnel('newBrandsLive')} active={funnelDrill === 'newBrandsLive'} />
        </Box>
        {funnelDrill === 'customersClosed' && data && (
          <Box sx={{ mt: 2 }}>
            <GxPagedTable variant="executive"
              rows={data.closedWonDeals || []}
              rowKey={(d, i) => d.hubspotDealId || i}
              emptyText="No closed-won marketing deals in this window."
              columns={[
                { key: 'brand', header: 'Closed-Won Deal (Brand)', sortValue: (d) => d.brandName, cell: (d) => <Box component="span" sx={{ fontWeight: 500 }}>{d.brandName}</Box> },
                { key: 'closed', header: 'Closed', title: 'Deal close date (HubSpot).', sortValue: (d) => d.closedDate ?? '', cell: (d) => (d.closedDate ? shortDate(d.closedDate) : '—') },
                { key: 'amount', header: 'Amount', title: 'HubSpot deal Amount (₹).', sortValue: (d) => d.amount, cell: (d) => currency(d.amount) },
              ]}
            />
          </Box>
        )}
        {funnelDrill === 'newBrandsLive' && data && (
          <Box sx={{ mt: 2 }}>
            <GxPagedTable variant="executive"
              rows={data.newActiveBrandRows || []}
              rowKey={(b) => b.brandId}
              emptyText="No new brands went live in this window."
              headerGroups={[periodLeadGroup(2, proship?.label), ...GROUPED_METRIC_GROUPS, { label: '', span: 3 }]}
              columns={[
                brandCol,
                { key: 'poc', header: 'Sales POC', title: "The account's Sales POC — the Data → Brands assignment on ProShip tables.", sortValue: (b) => b.salesPoc, cell: (b) => b.salesPoc },
                ...groupedMetricColumns(pickBrand),
                lastShipCol((b) => b.daysSinceShipment),
                { ...dealClosedCol, title: "Close date of the brand's won deal (HubSpot)" },
                { key: 'firstShip', header: 'First shipment', title: 'First-ever positive ProShip shipment', sortValue: (b) => b.firstShipment ?? '', cell: (b) => (b.firstShipment ? shortDate(b.firstShipment) : '—') },
              ]}
            />
          </Box>
        )}
      </Box>

      {/* ── Trends & Performance ───────────────────────────────────────────── */}
      <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
        <Box sx={{ mb: 2 }}><SectionTitle icon={<ShowChartIcon />}>Trends &amp; Performance</SectionTitle></Box>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
          <Box>
            <ChartHeader title="Monthly Shipments & Revenue Trend"
              tag={monthlyChart.length === 12 ? <ScopeTag label={`Trailing 12mo: ${monthlyChart[0].name} – ${monthlyChart[11].name}`} title="Always the last 12 calendar months, independent of the period filter above." /> : null} />
            <Box sx={{ mt: 1.5, height: 288 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyChart} margin={{ top: 8, right: 8, left: -18, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
                  <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 10, fill: GX_INK_500 }} />
                  <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11, fill: GX_INK_500 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: GX_INK_500 }} tickFormatter={(v) => compactCurrency(v)} />
                  <RTooltip content={<ChartTooltip moneyKeys={['revenue']} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="shipments" name="Shipments" fill="#c7d2fe" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#0d9488" strokeWidth={2.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box>
            <ChartHeader title="Last 4 Completed Weeks"
              tag={lastWeekEnd ? <ScopeTag label={`Complete weeks through ${shortDate(lastWeekEnd)}`} title="The last four fully completed ProShip weeks (Mon–Sun). Ends at the last complete Sunday, which can be up to 6 days before the data-through date." /> : null} />
            <Box sx={{ mt: 1.5, height: 288 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={weeklyChart} margin={{ top: 8, right: 8, left: -18, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
                  <XAxis dataKey="label" hide={!weeklyChart.length} tick={{ fontSize: 11, fill: GX_INK_500 }} />
                  <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11, fill: GX_INK_500 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: GX_INK_500 }} tickFormatter={(v) => compactCurrency(v)} />
                  <RTooltip content={<ChartTooltip moneyKeys={['revenue']} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="shipments" name="Shipments" fill="#a5b4fc" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="revenue" name="Revenue" fill="#5eead4" radius={[4, 4, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Channel & Brand Analysis ───────────────────────────────────────── */}
      <Box>
        <Box sx={{ mb: 1 }}><SectionTitle icon={<Inventory2OutlinedIcon />}>Channel &amp; Brand Analysis</SectionTitle></Box>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
          <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
            <ChartHeader title="Top 10 Brands by Revenue"
              tag={proship ? <ScopeTag label={`ProShip: ${proship.label}`} title="Revenue and shipments use the actual ProShip data-through date." /> : null} />
            <Box sx={{ mt: 1.5, height: 320, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topBrandsChart} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: GX_INK_500 }} tickFormatter={(v) => compactCurrency(v)} />
                  <YAxis type="category" dataKey="name" width={110} hide={!topBrandsChart.length} tick={{ fontSize: 11, fill: GX_INK_700 }} />
                  <RTooltip content={<ChartTooltip moneyKeys={['revenue']} />} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                    {topBrandsChart.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {topBrandsChart.length === 0 && !loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: INK_400 }}>No brand revenue in this window.</Box>
              )}
            </Box>
          </Box>
          <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
            <ChartHeader title="Deals by Marketing Source"
              tag={hubspot ? <ScopeTag label={`HubSpot: ${hubspot.label}`} title="Marketing-sourced deals created in the selected HubSpot window." /> : null} />
            <Box sx={{ position: 'relative', mt: 1.5, display: 'flex', height: 320, alignItems: 'center', justifyContent: 'center' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dealsBySource?.bySource || []} dataKey="deals" nameKey="source" innerRadius="62%" outerRadius="90%" paddingAngle={2}>
                    {(dealsBySource?.bySource || []).map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Pie>
                  <RTooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ pointerEvents: 'none', position: 'absolute', textAlign: 'center' }}>
                <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>{integer(dealsBySource?.total ?? 0)}</Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em', color: GX_INK_500 }}>Total Deals</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Revenue Concentration & Risk ───────────────────────────────────── */}
      <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<PieChartOutlineIcon />}>Revenue Concentration &amp; Risk</SectionTitle>
          {proship && <ScopeTag label={`ProShip: ${proship.label}`} title="Revenue concentration follows the actual ProShip data-through window." />}
        </Box>
        <Box sx={{ mt: 2, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
          {[['Top 5 Brands', concentration?.top5Pct], ['Top 10 Brands', concentration?.top10Pct], ['Top 20 Brands', concentration?.top20Pct]].map(([label, pct]) => (
            <Box key={label}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: GX_INK_700 }}>{label}</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: ACCENT }}>{pct != null ? percentage(pct) : '—'}</Typography>
              </Box>
              <Box sx={{ mt: 1, height: 8, overflow: 'hidden', borderRadius: '9999px', bgcolor: GX_BORDER }}>
                <Box sx={{ height: '100%', borderRadius: '9999px', width: `${pct ?? 0}%`, background: ACCENT_GRAD }} />
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 3, pt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, borderTop: `1px solid ${GX_BORDER}` }}>
          <Box>
            <Typography sx={subTitleSx}>Herfindahl-Hirschman Index (HHI)</Typography>
            <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>Revenue concentration risk assessment</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>{concentration?.hhi ?? '—'}</Typography>
            {concentration && (
              <Box component="span" sx={{ display: 'inline-flex', borderRadius: '9999px', border: '1px solid currentColor', bgcolor: '#fff', px: 1.5, py: 0.5, fontSize: 12, fontWeight: 500, color: hhiColor }}>{concentration.hhiLabel}</Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── At-Risk Brands, Churned & Declining ────────────────────────────── */}
      <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<WarningAmberIcon />} iconColor="#f59e0b">At-Risk Brands, Churned &amp; Declining</SectionTitle>
          {brandHealth?.dataThrough && <ScopeTag label={`As of ProShip: ${shortDate(brandHealth.dataThrough)}`} title="Churn = no positive shipment in the preceding 30 days. Declining compares the selected and equivalent prior ProShip windows." />}
        </Box>
        <Box sx={{ mt: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
          {[
            ['At Risk', 'atRisk', brandHealth?.atRiskCount ?? 0, 'Current shipments are under 70% of the prior 90-day daily run rate.', '#b45309'],
            ['Churned', 'churned', brandHealth?.churnedCount ?? 0, 'No positive shipment in the preceding 30 days.', '#e11d48'],
            ['Declining', 'declining', brandHealth?.decliningCount ?? 0, 'Revenue is lower than the equivalent previous ProShip window.', '#4f46e5'],
          ].map(([label, key, count, description, color]) => {
            const isOpen = openBrandDetail?.type === 'risk' && openBrandDetail.key === key
            return (
              <Box key={key} component="button" type="button" sx={tile3dSx} title={`Click to ${isOpen ? 'hide' : 'show'} ${label.toLowerCase()} brands`}
                onClick={() => setOpenBrandDetail(isOpen ? null : { type: 'risk', key, title: `${label} Brands` })}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: GX_INK_500 }}>{label}</Typography>
                <Typography sx={{ mt: 1, fontSize: 30, fontWeight: 700, lineHeight: 1.2, color }}>{integer(count)}</Typography>
                <Typography sx={{ mt: 1, fontSize: 12, lineHeight: '20px', color: GX_INK_500 }}>{description}</Typography>
                <Typography sx={{ mt: 1.5, fontSize: 12, fontWeight: 600, color }}>{isOpen ? 'Hide detail ↑' : 'View brands ↓'}</Typography>
              </Box>
            )
          })}
        </Box>
        {openBrandDetail?.type === 'risk' && brandHealth && (
          <DetailBlock title={openBrandDetail.title} subtitle={`Showing brands as of ProShip data through ${shortDate(brandHealth.dataThrough)}.`}>
            <BrandHealthTable rows={brandHealth[openBrandDetail.key] || []} timeframe={proship?.label} />
          </DetailBlock>
        )}
      </Box>

      {/* ── Brand Revenue by Age ───────────────────────────────────────────── */}
      <Box component="section" sx={{ ...cardSx, p: 2.5, mt: -1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<LayersOutlinedIcon />}>Brand Revenue by Age — New / Growth / Legacy</SectionTitle>
          {proship && <ScopeTag label={`ProShip: ${proship.label}`} title="Brand age is based on its first positive ProShip shipment." />}
        </Box>
        <Box sx={{ mt: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
          {(data?.cohorts || []).map((cohort, index) => {
            const accent = ['#4f46e5', '#d97706', '#059669'][index % 3]
            const isOpen = openBrandDetail?.type === 'cohort' && openBrandDetail.key === cohort.cohort
            return (
              <Box key={cohort.cohort} component="button" type="button" sx={tile3dSx} title={`Click to ${isOpen ? 'hide' : 'show'} brand detail`}
                onClick={() => setOpenBrandDetail(isOpen ? null : { type: 'cohort', key: cohort.cohort, title: `${cohort.cohort} Brands` })}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: GX_INK_500 }}>{cohort.cohort}</Typography>
                <Typography sx={{ mt: 1, fontSize: 20, fontWeight: 700, color: accent }}>{currency(cohort.revenue)}</Typography>
                <Typography sx={{ mt: 1, fontSize: 12, color: GX_INK_500 }}>{integer(cohort.brandCount)} brands · {integer(cohort.shipments)} shipments</Typography>
                <Box sx={{ mt: 1.5, height: 6, overflow: 'hidden', borderRadius: '9999px', bgcolor: GX_BORDER }}>
                  <Box sx={{ height: '100%', borderRadius: '9999px', width: `${cohort.sharePct}%`, background: ACCENT_GRAD }} />
                </Box>
                <Typography sx={{ mt: 0.5, textAlign: 'right', fontSize: 11, color: GX_INK_500 }}>{percentage(cohort.sharePct)} revenue share</Typography>
                <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 600, color: accent }}>{isOpen ? 'Hide detail ↑' : 'View brands ↓'}</Typography>
              </Box>
            )
          })}
        </Box>
        {openBrandDetail?.type === 'cohort' && (
          <DetailBlock title={openBrandDetail.title} subtitle={`Brand age is calculated from first positive ProShip shipment; dates through ${shortDate(proship?.to)}.`}>
            <CohortTable rows={(data?.cohorts || []).find((c) => c.cohort === openBrandDetail.key)?.brands || []} />
          </DetailBlock>
        )}
      </Box>

      {/* ── Sales POC Performance ──────────────────────────────────────────── */}
      <Box component="section" sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, pb: 1, pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <SectionTitle icon={<PeopleOutlineIcon />}>Sales POC Performance</SectionTitle>
            {proship && <ScopeTag label={`ProShip: ${proship.label}`} title="Uses the actual ProShip window and its equivalent prior comparison." />}
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>
            Equivalent-period revenue growth = (current window revenue − previous equal-length window revenue) ÷ previous window revenue. Click a column header to sort.
          </Typography>
        </Box>
        <Box component="button" type="button" onClick={() => setPocTableOpen((o) => !o)} sx={{
          mx: 2.5, mb: 2, width: 'calc(100% - 40px)', display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left', font: 'inherit', fontSize: 14,
          borderRadius: '12px', border: '1px solid rgba(79,70,229,0.18)', px: 2, py: 1.5, cursor: 'pointer', color: GX_INK_700,
          background: 'linear-gradient(90deg, rgba(79,70,229,0.07), rgba(6,182,212,0.05))', boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
          transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease, background 200ms ease',
          '&:hover': { transform: 'translateY(-2px)', borderColor: 'rgba(79,70,229,0.32)', background: 'linear-gradient(90deg, rgba(79,70,229,0.11), rgba(6,182,212,0.08))' },
        }}>
          {chevron(pocTableOpen, 16)}
          <span style={{ fontWeight: 600 }}>{pocTableOpen ? 'Hide Sales POC table' : 'Show Sales POC table'}</span>
        </Box>
        {pocTableOpen && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <GxPagedTable variant="executive"
              rows={data?.pocPerformance || []}
              rowKey={(p) => p.personId ?? p.owner}
              emptyText="No Sales POC shipment data in this window."
              columns={[
                { key: 'owner', header: 'Sales POC', title: "The account's Sales POC — the Data → Brands assignment on ProShip tables.", sortValue: (p) => p.owner,
                  cell: (p) => (
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {p.personId != null
                        ? <Link to={`/growthos/pocs-analytics/${p.personId}`} style={{ color: '#4338ca', textDecoration: 'none' }}>{p.owner}</Link>
                        : p.owner}
                      {p.team && <Box component="span" sx={{ ml: 0.5, display: 'inline-flex', borderRadius: '9999px', border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', px: 1.5, py: 0.25, fontSize: 12, fontWeight: 500, color: GX_INK_700 }}>{p.team}</Box>}
                    </Box>
                  ) },
                { key: 'activeBrands', header: 'Active Brands', sortValue: (p) => p.activeBrands, cell: (p) => integer(p.activeBrands) },
                { key: 'shipments', header: 'Shipments', sortValue: (p) => p.shipments, cell: (p) => integer(p.shipments) },
                { key: 'revenue', header: 'Revenue', sortValue: (p) => p.revenue, cell: (p) => currency(p.revenue) },
                { key: 'growth', header: 'Growth vs Prior', sortValue: (p) => p.momGrowthPct, cell: (p) => <GrowthPct pct={p.momGrowthPct} /> },
              ]}
            />
          </Box>
        )}
      </Box>

      {/* ── Revenue Projection ─────────────────────────────────────────────── */}
      <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SectionTitle icon={<ShowChartIcon />}>
            Revenue Projection
            {projection && (
              <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: INK_400 }}>
                · {new Date(`${projection.month}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
              </Box>
            )}
          </SectionTitle>
          {proship && <ScopeTag label={`ProShip through ${shortDate(proship.dataThrough)}`} title="Projection is always the true current month of ProShip data (anchored to data-through), independent of the period filter above." />}
        </Box>
        <Typography sx={{ mb: 2, mt: 0.5, fontSize: 12, color: GX_INK_500 }}>Always the current data month — independent of the period filter. Actuals through the ProShip data-through date, projected to month-end.</Typography>
        <Box sx={kpiGridSx}>
          <KpiCard label="Current Revenue" value={projection ? currency(projection.currentRevenue) : undefined} sub={projection ? `Day ${projection.dayOfMonth} of ${projection.daysInMonth}` : undefined} accent="indigo" icon={<CurrencyRupeeIcon />} loading={loading} />
          <KpiCard label="Projected Month-End" value={projection ? currency(projection.projectedMonthEnd) : undefined} sub="Current ÷ days elapsed × days in month" accent="teal" icon={<TrendingUpIcon />} loading={loading} />
          <KpiCard label="3-Month Run Rate" value={projection ? currency(projection.threeMonthRunRate) : undefined} sub="Avg. daily rate, prior 3 complete months" accent="amber" icon={<Inventory2OutlinedIcon />} loading={loading} />
          <KpiCard label="Projected Annual" value={projection ? currency(projection.projectedAnnual) : undefined} sub="3-month run rate × 12" accent="rose" icon={<TrackChangesIcon />} loading={loading} />
        </Box>
        {projection && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 12, color: GX_INK_500 }}>
              <Box component="span" sx={{ fontWeight: 600, color: GX_INK_700 }}>Current vs Projected Month-End</Box>
              <span>Day {projection.dayOfMonth} of {projection.daysInMonth}</span>
            </Box>
            <Box sx={{ mt: 1, height: 10, overflow: 'hidden', borderRadius: '9999px', bgcolor: GX_BORDER }}>
              <Box sx={{ height: '100%', borderRadius: '9999px', width: `${Math.min(100, pctOf(projection.currentRevenue, projection.projectedMonthEnd))}%`, background: ACCENT_GRAD }} />
            </Box>
            <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: INK_400 }}>
              <span>Current: {currency(projection.currentRevenue)}</span>
              <span>Projected: {currency(projection.projectedMonthEnd)}</span>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
